import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

function fracStr(num: number, den: number): string {
  if (num === 0) return '0';
  const g = gcd(Math.abs(num), den);
  const n = num / g;
  const d = den / g;
  if (d === 1) return String(n);
  const whole = Math.floor(n / d);
  const rem = n % d;
  if (whole > 0 && rem > 0) return `${whole}又${rem}/${d}`;
  if (whole > 0) return String(whole);
  return `${n}/${d}`;
}

/**
 * N-4-5：同分母分數加減（四上版，結果可超過 1）。
 * 四上第五章：同分母分數加減，含真分數→帶分數、帶分數加減。
 *
 * difficulty 1：真分數 + 真分數（同分母，分母 3–8，結果 ≤ 1）
 * difficulty 2：真分數 + 真分數（同分母，結果可超過 1，帶分數）
 * difficulty 3：帶分數 + 真分數 或 帶分數 − 真分數
 */
export function generateFracAddSameDenom(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);
  const isAdd = difficulty < 3 ? true : rand() > 0.4;
  const denomMax = difficulty === 1 ? 8 : difficulty === 2 ? 12 : 10;
  const denomMin = difficulty === 1 ? 3 : 4;
  const den = randInt(rand, denomMin, denomMax);

  let prompt: string;
  let correct: string;
  let options: string[];
  let explanation: string;

  if (difficulty === 1) {
    // a/den + b/den ≤ 1
    const a = randInt(rand, 1, den - 2);
    const b = randInt(rand, 1, den - a);
    const sum = a + b;
    correct = fracStr(sum, den);
    prompt = `${a}/${den} + ${b}/${den} = ?`;
    const w1 = fracStr(sum + 1, den);
    const w2 = fracStr(Math.max(1, sum - 1), den);
    const w3 = `${a + b}/${den + den}`;
    options = [correct, w1, w2, w3];
    explanation = `同分母相加，分母不變：${a}+${b}=${sum}，結果 ${correct}`;
  } else if (difficulty === 2) {
    // a/den + b/den, result may exceed 1
    const a = randInt(rand, Math.ceil(den / 2), den - 1);
    const b = randInt(rand, Math.ceil(den / 2), den - 1);
    const sum = a + b;
    correct = fracStr(sum, den);
    prompt = `${a}/${den} + ${b}/${den} = ?`;
    const w1 = fracStr(sum + 1, den);
    const w2 = `${a}/${den}`;
    const whole = Math.floor(sum / den);
    const rem = sum % den;
    const w3 = rem > 0 ? `${whole}/${den}` : fracStr(sum - 1, den);
    options = [correct, w1, w2, w3];
    explanation = `${a}+${b}=${sum}，${sum}/${den} = ${correct}`;
  } else {
    // Mixed number ± proper fraction
    const wholeA = randInt(rand, 1, 3);
    const numA = randInt(rand, 1, den - 1);
    if (isAdd) {
      const numB = randInt(rand, 1, den - 1);
      const sumNum = numA + numB;
      const totalWhole = wholeA + Math.floor(sumNum / den);
      const remFrac = sumNum % den;
      correct = remFrac > 0 ? `${totalWhole}又${remFrac}/${den}` : String(totalWhole);
      prompt = `${wholeA}又${numA}/${den} + ${numB}/${den} = ?`;
      explanation = `分數部分 ${numA}+${numB}=${sumNum}，整數部分 ${wholeA}+${Math.floor(sumNum / den)}=${totalWhole}，結果 ${correct}`;
      const w1 = `${totalWhole + 1}又${remFrac > 0 ? remFrac : 1}/${den}`;
      const w2 = `${wholeA}又${(numA + numB) % den || numA}/${den}`;
      const w3 = remFrac > 0 ? `${totalWhole}/${den}` : `${totalWhole - 1}又${den - 1}/${den}`;
      options = [correct, w1, w2, w3];
    } else {
      const numB = randInt(rand, 1, numA > 0 ? numA : den - 1);
      const diffNum = numA - numB;
      if (diffNum >= 0) {
        correct = diffNum > 0 ? `${wholeA}又${diffNum}/${den}` : String(wholeA);
        prompt = `${wholeA}又${numA}/${den} − ${numB}/${den} = ?`;
        explanation = `${numA}−${numB}=${diffNum}，結果 ${correct}`;
      } else {
        const newWhole = wholeA - 1;
        const newNum = den + diffNum;
        correct = newNum > 0 ? `${newWhole}又${newNum}/${den}` : String(newWhole);
        prompt = `${wholeA}又${numA}/${den} − ${numB}/${den} = ?`;
        explanation = `需要借位：${wholeA - 1}又${den + numA}/${den} − ${numB}/${den} = ${correct}`;
      }
      const w1 = `${wholeA}又${numA}/${den}`;
      const w2 = `${wholeA - 1}又${numA}/${den}`;
      const w3 = `${wholeA}又${numB}/${den}`;
      options = [correct, w1, w2, w3];
    }
  }

  // Ensure 4 unique options
  const seen = new Set<string>();
  const uniqueOptions: string[] = [];
  for (const o of options) {
    if (!seen.has(o)) { seen.add(o); uniqueOptions.push(o); }
  }
  while (uniqueOptions.length < 4) {
    const extra = `${randInt(rand, 1, den - 1)}/${den}`;
    if (!seen.has(extra)) { seen.add(extra); uniqueOptions.push(extra); }
    else uniqueOptions.push(String(randInt(rand, 1, 5)));
  }

  const shuffled = uniqueOptions.slice(0, 4).sort((x, y) => {
    const h = (s: string) => s.split('').reduce((a, c) => a + c.charCodeAt(0), seed);
    return mulberry32(h(x))() - mulberry32(h(y))();
  });

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.frac.add.same-denom.4up',
  };
}
